import { View, Text, Button, Alert, StyleSheet, Pressable, ScrollView, FlatList, RefreshControl, Image as RNImage, TouchableOpacity } from 'react-native'
import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { DeviceEventEmitter } from 'react-native';
import { StackNavigationProp } from '@react-navigation/stack';
import ScreenWrapper from '../../Components/ScreenWrapper'
import { Supabase } from '../../Utils/supabase.config';
import { useAuth } from '../../Utils/AuthProvider'
import { theme } from '../../constants/theme'
import { User } from '../../../assets/icons'
import { hp, wp } from '../../helpers/common'
import { fetchPosts, fetchPostsWithCursor } from '../../services/postService'
import PostCard from '../../Components/PostCard'
import { getSupabaseFileUrl } from '../../services/imageService'
import Loading from '../../Components/Loading'
import { getUserData, canAccessAdminFeatures } from '../../services/userService'
import { getUnreadAnnouncementCount } from '../../services/announcementService'
import { getDisabledMenuItems } from '../../services/configService'
import Avatar from '../../Components/Avatar'
import defaultUserImg from '../../../assets/images/defaultuser.png';

// Define types for posts and payloads
interface Post {
  id: number;
  body?: string;
  file?: string | string[];
  created_at: string;
  userId: string;
  is_deal?: boolean;
  user?: {
    id: string;
    name: string;
    image?: string;
  };
  postLikes?: any[];
  comments?: any[];
}

interface PostResponse {
  success: boolean;
  data?: Post[];
  nextCursor?: string | null;
  hasMore?: boolean;
  msg?: string;
}

interface PostPayload {
  eventType: string;
  new?: Post;
  old?: Post;
}

interface NotificationPayload {
  eventType: string;
  new?: {
    id: string;
  };
}

// Define navigation type
type RootStackParamList = {
  Home: undefined;
  profile: undefined;
  notifications: undefined;
  newPost: undefined;
  editProfile: undefined;
  search: undefined;
  Communities: undefined;
  Announcements: undefined;
  PollsList: undefined;
  CreatePoll: undefined;
  PollDetails: { pollId: string };
  SpinWheelsList: undefined;
  SpinWheel: { wheelId: string };
  Help: undefined;
  AdminReports: undefined;
  postDetails: { postId: string | number };
  ReportPost: { postId: string | number };  
  Deals: undefined;
};

type NavigationProp = StackNavigationProp<RootStackParamList>;

const HomeScreen = () => {
    const {user, setAuth} = useAuth(); 
    const { userData } = useAuth();
    const navigation = useNavigation<NavigationProp>();
    
    const [posts, setPosts] = useState<Post[]>([]);
    const [hasMore, setHasMore] = useState(true);
    const [authorAdminMap, setAuthorAdminMap] = useState<Record<string, boolean>>({});
    const [communityLinkAllowedMap, setCommunityLinkAllowedMap] = useState<Record<string, boolean>>({});
    const [nextCursor, setNextCursor] = useState<string | null>(null);
    const [notificationCount, setNotificationCount] = useState(0);
    const [announcementCount, setAnnouncementCount] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true); // Track initial load
    const [canAccessAdmin, setCanAccessAdmin] = useState(false);
    const [disabledMenuItems, setDisabledMenuItems] = useState<string[]>([]);

    // Check admin permissions and load disabled menu items when user changes
    useEffect(() => {
        if (user?.id) {
            checkAdminPermissions();
            loadAnnouncementCount();
            loadDisabledMenuItems();
        }
    }, [user, userData?.role]);

    const checkAdminPermissions = async () => {
        if (!user?.id) return;
        
        const result = await canAccessAdminFeatures(user.id);
        if (result.success) {
            setCanAccessAdmin(result.data);
        }
    };

    const loadAnnouncementCount = async () => {
        if (!user?.id) return;
        
        try {
            const result = await getUnreadAnnouncementCount(user.id);
            if (result.success && typeof result.data === 'number') {
                setAnnouncementCount(result.data);
            }
        } catch (error) {
            console.error('Error loading announcement count:', error);
        }
    };

    const loadDisabledMenuItems = async () => {
        try {
            // Get user role, default to 'Guest' if not set
            const userRole = userData?.role || 'guest';
            const disabled = await getDisabledMenuItems(userRole);
            setDisabledMenuItems(disabled || []);
        } catch (error) {
            console.error('Error loading disabled menu items:', error);
            setDisabledMenuItems([]);
        }
    };

    // Helper function to check if menu item should be shown
    const shouldShowMenuItem = (menuItemId: string) => {
        return !disabledMenuItems.includes(menuItemId);
    };

    // Use useFocusEffect to refresh posts when screen comes into focus
    useFocusEffect(
      React.useCallback(() => {
        // Reset pagination and refresh posts
        setNextCursor(null);
        setHasMore(true);
        getPosts();
        loadAnnouncementCount();

        return () => {
          // Pause all videos when leaving Home screen
          try { DeviceEventEmitter.emit('pauseAllVideos'); } catch (_) {}
        };
      }, [])
    );

    // Track manually added post IDs to prevent duplicates from real-time subscription
    const manuallyAddedPostIds = useRef<Set<number | string>>(new Set());

    // Listen for new post creation event from newPost screen
    useEffect(() => {
      const newPostListener = DeviceEventEmitter.addListener('newPostCreated', async (newPostData: any) => {
        // Immediately add the new post to the list
        if (newPostData && newPostData.id) {
          // Mark this post as manually added to prevent duplicate from real-time subscription
          manuallyAddedPostIds.current.add(newPostData.id);
          
          // Fetch user data for the new post
          let res = await getUserData(newPostData.userId);
          const postWithUser = {
            ...newPostData,
            user: res.success && res.data ? {
              id: res.data.id || '',
              name: res.data.name || '',
              image: res.data.image
            } : undefined,
            postLikes: [],
            comments: [{count: 0}]
          };
          
          // Add to the beginning of the posts list
          setPosts(prevPosts => {
            // Filter out any existing post with the same ID first, then add the new one
            const filtered = prevPosts.filter(p => p.id !== postWithUser.id);
            return [postWithUser, ...filtered];
          });
          
          // Clear the manual flag after a delay to allow real-time updates later
          setTimeout(() => {
            manuallyAddedPostIds.current.delete(newPostData.id);
          }, 5000);
        }
      });

      return () => {
        newPostListener.remove();
      };
    }, []);

    // const onLogout = async () => {
    //     setAuth(null);
    //     const {error} = await supabase.auth.signOut();
    //     if (error) {
    //       Alert.alert("Error Signing Out User", error.message);
    //     }
    // }

    const handlePostEvent = async (payload: any)=>{
      if(payload.eventType == 'INSERT' && payload?.new?.id){
        // Skip if this post was manually added (to prevent duplicate)
        if (manuallyAddedPostIds.current.has(payload.new.id)) {
          return;
        }
        
        let newPost = {...payload.new};
        let res = await getUserData(newPost.userId);
        newPost.user = res.success && res.data ? {
          id: res.data.id || '',
          name: res.data.name || '',
          image: res.data.image
        } : undefined;
        newPost.postLikes = []; // while adding likes
        newPost.comments = [{count: 0}] // while adding comments
        setPosts(prevPosts=> {
          // Filter out any existing post with the same ID first, then add the new one
          const filtered = prevPosts.filter(p => p.id !== newPost.id);
          return [newPost, ...filtered];
        });
      }

      if(payload.eventType == 'DELETE' && payload?.old?.id){
        setPosts(prevPosts=> {
          let updatedPosts = prevPosts.filter(post=> post.id!=payload.old!.id);
          return updatedPosts;
        })
      }

      if(payload.eventType == 'UPDATE' && payload?.new?.id){
        setPosts(prevPosts=> {
          let updatedPosts = prevPosts.map(post=> {
            if(post.id==payload.new!.id){
              post.body = payload.new!.body;
              post.file = payload.new!.file;
            }
            return post;
          });
          return updatedPosts;
        })
      }
    }

    const handleNewNotification = (payload: any)=>{
      if(payload.eventType=='INSERT' && payload?.new?.id){
        setNotificationCount(prev=> prev+1);
      }
    }

    const handleLikesEvent = (payload: any) => {
      if (payload.eventType === 'INSERT') {
        setPosts(prevPosts => prevPosts.map(post => {
          if (post.id === payload.new.postId) {
            return {
              ...post,
              postLikes: [...(post.postLikes || []), payload.new]
            };
          }
          return post;
        }));
      } else if (payload.eventType === 'DELETE') {
        setPosts(prevPosts => prevPosts.map(post => {
          if (post.id === payload.old.postId) {
            return {
              ...post,
              postLikes: (post.postLikes || []).filter((like: any) => 
                !(like.userId === payload.old.userId && like.postId === payload.old.postId)
              )
            };
          }
          return post;
        }));
      }
    };

    const handleCommentsEvent = (payload: any) => {
      if (payload.eventType === 'INSERT') {
        setPosts(prevPosts => prevPosts.map(post => {
          if (post.id === payload.new.postId) {
            const currentCount = post.comments?.[0]?.count || 0;
            return {
              ...post,
              comments: [{ count: currentCount + 1 }]
            };
          }
          return post;
        }));
      } else if (payload.eventType === 'DELETE') {
        setPosts(prevPosts => prevPosts.map(post => {
          if (post.id === payload.old.postId) {
            const currentCount = post.comments?.[0]?.count || 0;
            return {
              ...post,
              comments: [{ count: Math.max(0, currentCount - 1) }]
            };
          }
          return post;
        }));
      }
    };

    const handleAnnouncementEvent = (payload: any) => {
      if (payload.eventType === 'INSERT' && payload?.new?.is_announcement) {
        // New announcement posted, increment counter
        setAnnouncementCount(prev => prev + 1);
      }
    };

    useEffect(() => {
      if (!user) return; // Don't subscribe if user is not available

      let postChannel = Supabase
        .channel('posts')
        .on('postgres_changes' as any, { event: '*', schema: 'public', table: 'posts' } as any, handlePostEvent)
        .subscribe();

      let notificationChannel = Supabase
        .channel('notifications')
        .on('postgres_changes' as any, { event: 'INSERT', schema: 'public', table: 'notifications', filter: `receiverId=eq.${user.id}` } as any, handleNewNotification)
        .subscribe();

      let likesChannel = Supabase
        .channel('post-likes')
        .on('postgres_changes' as any, { event: '*', schema: 'public', table: 'post_likes' } as any, handleLikesEvent)
        .subscribe();

      let commentsChannel = Supabase
        .channel('post-comments')
        .on('postgres_changes' as any, { event: '*', schema: 'public', table: 'comments' } as any, handleCommentsEvent)
        .subscribe();

      let announcementChannel = Supabase
        .channel('announcements')
        .on('postgres_changes' as any, { event: 'INSERT', schema: 'public', table: 'posts', filter: 'is_announcement=eq.true' } as any, handleAnnouncementEvent)
        .subscribe();

      return () => {
        Supabase.removeChannel(postChannel);
        Supabase.removeChannel(notificationChannel);
        Supabase.removeChannel(likesChannel);
        Supabase.removeChannel(commentsChannel);
        Supabase.removeChannel(announcementChannel);
      };
    }, [user]);
    
    const getPosts = async (isLoadMore = false) => {
      if (!hasMore && isLoadMore) {
        return null; // if no more posts then don't call the api
      }
      
      // Set loading states
      if (isLoadMore) {
        setRefreshing(true);
      } else {
        // Initial load - show loading spinner
        setInitialLoading(true);
      }
      
      try {
        const limit = 10; // Fixed page size - STRICT LIMIT
        const cursor = isLoadMore ? nextCursor : null;
        
        let res = await fetchPostsWithCursor(limit, null, cursor as any) as PostResponse;
        
        if (res.success && res.data) {
          // Normalize comment structure to ensure consistency
          const normalizedData = res.data.map((post: any) => ({
            ...post,
            comments: post.comments && Array.isArray(post.comments) && post.comments.length > 0 
              ? post.comments 
              : [{count: 0}]
          }));

          // Update community link-allowed map from this batch
          setCommunityLinkAllowedMap(prev => {
            const next = { ...prev };
            normalizedData.forEach((post: any) => {
              const communityId = String(post?.community_id || post?.communityId || '');
              const isAllowed = post?.community && typeof post.community.is_linkallowed === 'boolean'
                ? Boolean(post.community.is_linkallowed)
                : undefined;
              if (communityId && typeof isAllowed === 'boolean' && next[communityId] === undefined) {
                next[communityId] = isAllowed;
              }
            });
            return next;
          });

          // Batch-check admin status for (author, community) pairs in this batch
          try {
            const pairs: { key: string; userId: string; communityId: string }[] = [];
            const seen = new Set<string>();
            normalizedData.forEach((post: any) => {
              const userId = String(post?.userId || post?.user_id || post?.user?.id || '');
              const communityId = String(post?.community_id || post?.communityId || '');
              if (!userId || !communityId) return;
              const key = `${userId}|${communityId}`;
              if (authorAdminMap[key] !== undefined || seen.has(key)) return;
              seen.add(key);
              pairs.push({ key, userId, communityId });
            });

            if (pairs.length > 0) {
              const results = await Promise.all(
                pairs.map(async ({ key, userId, communityId }) => {
                  try {
                    const { data, error } = await Supabase.rpc('check_user_community_admin', {
                      user_uuid: userId,
                      community_uuid: communityId,
                    });
                    if (error) {
                      return { key, isAdmin: false };
                    }
                    return { key, isAdmin: Boolean(data) };
                  } catch {
                    return { key, isAdmin: false };
                  }
                })
              );

              setAuthorAdminMap(prev => {
                const next = { ...prev };
                results.forEach(r => {
                  next[r.key] = r.isAdmin;
                });
                return next;
              });
            }
          } catch {
            // If RPC fails, links will stay plain text (safe fallback)
          }

          if (isLoadMore) {
            // Check for duplicates before appending
            setPosts(prev => {
              const existingIds = new Set(prev.map(post => post.id));
              const newPosts = normalizedData.filter(post => !existingIds.has(post.id));
              // Also ensure no duplicates in the new posts array itself
              const uniqueNewPosts = newPosts.filter((post, index, self) => 
                index === self.findIndex(p => p.id === post.id)
              );
              return [...prev, ...uniqueNewPosts];
            });
          } else {
            // Replace posts for initial load or refresh, ensuring no duplicates
            const uniquePosts = normalizedData.filter((post, index, self) => 
              index === self.findIndex(p => p.id === post.id)
            );
            setPosts(uniquePosts as Post[]);
          }
          
          // Update cursor and hasMore for next page
          if (res.nextCursor !== undefined && res.hasMore !== undefined) {
            setNextCursor(res.nextCursor);
            setHasMore(res.hasMore);
          } else {
            // If no cursor/hasMore in response, assume no more posts
            setHasMore(false);
          }
        } else {
        }
      } catch (error) {
        // Swallow errors here; they are handled upstream via UI state.
      } finally {
        // Always reset loading states when done
        if (isLoadMore) {
          setRefreshing(false);
        } else {
          // Initial load complete
          setInitialLoading(false);
        }
      }
    }

    const onRefresh = async () => {
      setRefreshing(true);
      setNextCursor(null);
      setHasMore(true);
      await getPosts();
      setRefreshing(false);
      setInitialLoading(false); // Ensure initial loading is false after refresh
    };

    // Memoized renderItem function to prevent unnecessary re-renders
    // Track which items are visible
    

    const handlePostDelete = useCallback(() => {}, []);
    const handlePostEdit = useCallback(() => {}, []);

    // No video visibility tracking needed - videos open in fullscreen

    const renderPostItem = useCallback(({ item, index }: { item: Post, index: number }) => {
      const PostCardComponent = PostCard as any;
      const communityId = String((item as any)?.community_id || (item as any)?.communityId || '');
      const userId = String((item as any)?.userId || (item as any)?.user_id || (item as any)?.user?.id || '');
      const adminKey = `${userId}|${communityId}`;
      const allowLinks =
        Boolean(communityId && communityLinkAllowedMap[communityId]) &&
        Boolean(userId && authorAdminMap[adminKey]);

      return (
        <PostCardComponent 
          item={item} 
          currentUser={user}
          showMoreIcon={true}
          hasShadow={true}
          showDelete={false}
          onDelete={handlePostDelete}
          onEdit={handlePostEdit}
          showReportSpam={true}
          allowLinks={allowLinks}
        />
      );
    }, [user, handlePostDelete, handlePostEdit, authorAdminMap, communityLinkAllowedMap]);

    // Memoized keyExtractor function
    const keyExtractor = useCallback((item: Post) => item.id.toString(), []);

    // REMOVED: Automatic load on scroll - replaced with "Load More" button
    // This prevents automatic loading of all posts on page load
    const handleLoadMore = useCallback(() => {
      if (!hasMore || refreshing) {
        return;
      }
      getPosts(true); // Pass true for load more
    }, [hasMore, refreshing, nextCursor]);

    // Memoized getItemLayout function for better performance
    const getItemLayout = useCallback((data: any, index: number) => {
      // Slightly larger estimate to minimize blank gaps during fast scroll
      const estimatedItemHeight = 360;
      return {
        length: estimatedItemHeight,
        offset: estimatedItemHeight * index,
        index,
      };
    }, []);

  return (
    <ScreenWrapper bg="white">
      <View style={styles.container}>
        {/* header */}
        <View style={styles.header}>
          <ScrollView 
            horizontal 
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.iconsScrollContainer}
          >
            {/* Notifications - Menu ID: notifications */}
            {shouldShowMenuItem('notifications') && (
              <Pressable onPress={()=> {
                setNotificationCount(0);
                navigation.navigate('notifications');
              }}>
                <Text style={styles.emojiIcon}>💖</Text>
                {
                  notificationCount>0 && (
                    <View style={styles.pill}>
                      <Text style={styles.pillText}>{notificationCount}</Text>
                    </View>
                  )
                }
              </Pressable>
            )}
            
            {/* Deals - Menu ID: deals */}
            {shouldShowMenuItem('deals') && (
              <Pressable onPress={()=> navigation.navigate('Deals')}>
                <Text style={styles.emojiIcon}>🛍️</Text>
              </Pressable>
            )}
            
            {/* Communities - Menu ID: communities */}
            {shouldShowMenuItem('communities') && (
              <Pressable onPress={()=> navigation.navigate('Communities')}>
                <Text style={styles.emojiIcon}>👥</Text>
              </Pressable>
            )}
            
            {/* Announcements - Menu ID: announcements */}
            {shouldShowMenuItem('announcements') && (
              <Pressable onPress={()=> {
                setAnnouncementCount(0);
                navigation.navigate('Announcements');
              }}>
                <Text style={styles.emojiIcon}>📢</Text>
                {
                  announcementCount > 0 && (
                    <View style={styles.pill}>
                      <Text style={styles.pillText}>{announcementCount}</Text>
                    </View>
                  )
                }
              </Pressable>
            )}
            
            {/* Polls - Menu ID: polls */}
            {shouldShowMenuItem('polls') && (
              <Pressable onPress={()=> navigation.navigate('PollsList')}>
                <Text style={styles.emojiIcon}>📊</Text>
              </Pressable>
            )}
            
            {/* Spin Wheels - Menu ID: spinwheels */}
            {shouldShowMenuItem('spinwheels') && (
              <Pressable onPress={()=> navigation.navigate('SpinWheelsList')}>
                <Text style={styles.emojiIcon}>🎰</Text>
              </Pressable>
            )}
            
            {/* Help - Menu ID: help */}
            {shouldShowMenuItem('help') && (
              <Pressable onPress={()=> navigation.navigate('Help')}>
                <Text style={styles.emojiIcon}>❓</Text>
              </Pressable>
            )}
            
            {/* Admin Reports - Menu ID: admin - Only visible to admins */}
            {canAccessAdmin && shouldShowMenuItem('admin') && (
              <Pressable onPress={()=> navigation.navigate('AdminReports')}>
                <Text style={styles.emojiIcon}>📈</Text>
              </Pressable>
            )}
            
            {/* Profile - Always visible */}
            <Pressable onPress={()=> navigation.navigate('profile')}>
              <Avatar 
                uri={userData?.image || defaultUserImg}
                size={hp(3.8)}
                rounded={theme.radius.sm}
                style={{ borderWidth: 2 }}
              />
            </Pressable>
          </ScrollView>
        </View>

        {/* posts */}
        {initialLoading ? (
          // Show loading spinner during initial load
          <View style={styles.loadingContainer}>
            <Loading />
            <Text style={styles.loadingText}>Loading posts...</Text>
          </View>
        ) : (
          <FlatList
            data={posts}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.listStyle}
            keyExtractor={keyExtractor}
            renderItem={renderPostItem}
            getItemLayout={getItemLayout}
            // REMOVED: onEndReached - causes automatic loading of all posts
            // onEndReached={handleEndReached}
            // onEndReachedThreshold={0.5}
            removeClippedSubviews={true}
            windowSize={5} // Reduced from 7 to 5 for better performance - only render 5 screens worth of items
            initialNumToRender={5} // Reduced from 6 to 5 - render fewer items initially
            maxToRenderPerBatch={5} // Reduced from 8 to 5 - render fewer items per batch
            updateCellsBatchingPeriod={100} // Increased from 50 to 100ms - batch updates less frequently
            maintainVisibleContentPosition={null} // Disable for better performance
            ListFooterComponent={
              hasMore && posts.length > 0 ? (
                // Only show "Load More" button if we have posts and more to load
                <View style={styles.loadMoreContainer}>
                  <TouchableOpacity 
                    onPress={() => {
                      if (__DEV__) {
                        console.log('[HomeScreen] Load More button pressed');
                      }
                      handleLoadMore();
                    }}
                    style={[styles.loadMoreButton, refreshing && styles.loadMoreButtonDisabled]}
                    disabled={refreshing}
                    activeOpacity={0.7}
                  >
                    <Text style={styles.loadMoreText}>
                      {refreshing ? 'Loading...' : 'Load More Posts'}
                    </Text>
                  </TouchableOpacity>
                </View>
              ) : !hasMore && posts.length > 0 ? (
                <View style={{marginVertical: 30}}>
                  <Text style={styles.noPosts}>No more posts</Text>
                </View>
              ) : posts.length === 0 ? (
                <View style={styles.emptyContainer}>
                  <Text style={styles.emptyText}>No posts yet. Be the first to post!</Text>
                </View>
              ) : null
            }
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={onRefresh}
                colors={[theme.colors.primary]}
                tintColor={theme.colors.primary}
              />
            }
          />
        )}

        {/* <Button onPress={onLogout} title="Logout" /> */}
      </View>
      
      

</ScreenWrapper>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    // paddingHorizontal: wp(4)
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: 10,
    marginHorizontal: wp(4)
  },
  iconsScrollContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingRight: 10,
    flexGrow: 1,
    justifyContent: 'flex-end',
  },
  title: {
    color: theme.colors.text,
    fontSize: hp(3.2),
    fontWeight: '700'
  },
  avatarImage: {
    height: hp(4.3),
    width: hp(4.3),
    borderRadius: theme.radius.sm,
    borderCurve: 'continuous',
    borderColor: theme.colors.gray,
    borderWidth: 3
  },
  icons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 18
  },
  listStyle: {
    paddingTop: 20,
    paddingHorizontal: wp(4)
  },
  noPosts: {
    fontSize: hp(2),
    textAlign: 'center',
    color: theme.colors.text
  },
  loadMoreContainer: {
    marginVertical: 30,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadMoreButton: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: wp(8),
    paddingVertical: hp(1.5),
    borderRadius: theme.radius.lg,
    minWidth: wp(40),
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadMoreText: {
    color: 'white',
    fontSize: hp(1.8),
    fontWeight: theme.fonts.semibold || '600',
  },
  loadMoreButtonDisabled: {
    opacity: 0.6,
  },
  pill: {
    position: 'absolute',
    right: -10,
    top: -4,
    height: hp(2.2),
    width: hp(2.2),
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 20,
    backgroundColor: theme.colors.roseLight
  },
  pillText: {
    color: 'white',
    fontSize: hp(1.2),
    fontWeight: '700'
  },
  emojiIcon: {
    fontSize: hp(2.8),
    color: theme.colors.text,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: hp(20),
  },
  loadingText: {
    marginTop: hp(2),
    fontSize: hp(1.8),
    color: theme.colors.textLight,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: hp(20),
  },
  emptyText: {
    fontSize: hp(2),
    color: theme.colors.textLight,
    textAlign: 'center',
  },
})

export default HomeScreen